/**
 * P0 Bugfix — Reminder Alerts (Feb 2026)
 *
 * Globale provider die elke 45s `/api/admin/reminders/due` polled,
 * een centrale pop-up modal toont, een geluidssignaal genereert via
 * Web Audio API (geen externe audio-file nodig), en de Bell-badge in
 * de sidebar voorziet van een open-count.
 *
 * Anti-spam strategie:
 *  - Backend stuurt slechts één e-mail per reminder (idempotent).
 *  - Frontend houdt een Set van eerder geziene reminder-IDs bij, zodat
 *    we alleen geluid afspelen bij echt NIEUWE due reminders.
 *  - "mark-shown" wordt server-side gemarkeerd zodra de modal opent,
 *    enkel voor audit. De pop-up zelf is niet geblokkeerd door dit
 *    veld zodat een refresh de medewerker opnieuw waarschuwt.
 *  - Snooze / dismiss / afronden roept de juiste backend endpoint en
 *    verwijdert het item uit de UI; volgende poll bevestigt de stilte.
 *
 * Browser autoplay-policy:
 *  - Veel browsers blokkeren `audioContext.resume()` voordat een
 *    user-gesture heeft plaatsgevonden. We bieden een knop "Geluid
 *    activeren" boven in de modal aan zolang de AudioContext nog
 *    suspended is.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bell, AlertTriangle, Volume2, VolumeX, X, Clock, Check, ExternalLink, Loader2,
} from "lucide-react";

import { adminApi, useAuth, formatApiError } from "../../context/AuthContext";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

const POLL_INTERVAL_MS = 45_000; // elke 45 seconden
const STORAGE_AUDIO_ENABLED = "rem_alerts_audio_enabled";

const ReminderAlertCtx = createContext({
  openCount: 0,
  overdueCount: 0,
  openModal: () => {},
});

export const useReminderAlerts = () => useContext(ReminderAlertCtx);

function playBeep(audioCtxRef) {
  try {
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state !== "running") return false;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(660, ctx.currentTime + 0.18);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.36);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
    return true;
  } catch {
    return false;
  }
}

function buildAudioCtx() {
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    return new C();
  } catch {
    return null;
  }
}

export default function ReminderAlertProvider({ children, onNavigateToReminder }) {
  const { user, hasPermission } = useAuth();
  const canView = !!user && hasPermission("reminders:view");

  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioState, setAudioState] = useState("idle"); // idle | running | suspended | unsupported
  const [busyItem, setBusyItem] = useState(null);

  const audioCtxRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const initialPollDoneRef = useRef(false);
  const lastShownAtServerRef = useRef(new Set());

  // ---- Audio (gebruikersgesture om autoplay-policy te respecteren) ----
  const enableAudio = useCallback(async () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = buildAudioCtx();
    }
    const ctx = audioCtxRef.current;
    if (!ctx) {
      setAudioState("unsupported");
      return;
    }
    try {
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      setAudioState(ctx.state);
      try {
        localStorage.setItem(STORAGE_AUDIO_ENABLED, "1");
      } catch { /* storage blocked */ }
      // korte feedback-beep zodat de gebruiker weet dat het werkt
      playBeep(audioCtxRef);
      toast.success("Geluidssignaal voor reminders geactiveerd");
    } catch {
      setAudioState("suspended");
    }
  }, []);

  // Auto-create AudioContext at mount; activate later via user-click.
  useEffect(() => {
    if (!canView) return;
    if (!audioCtxRef.current) {
      audioCtxRef.current = buildAudioCtx();
    }
    const ctx = audioCtxRef.current;
    if (!ctx) {
      setAudioState("unsupported");
      return;
    }
    setAudioState(ctx.state);
    // Wanneer de gebruiker eerder audio activeerde, proberen we het stilletjes
    let cancelled = false;
    (async () => {
      try {
        if (localStorage.getItem(STORAGE_AUDIO_ENABLED) === "1" && ctx.state === "suspended") {
          await ctx.resume();
          if (!cancelled) setAudioState(ctx.state);
        }
      } catch { /* user-gesture nodig */ }
    })();
    return () => { cancelled = true; };
  }, [canView]);

  // ---- Polling ----
  const fetchDue = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const r = await adminApi.get("/admin/reminders/due", {
        params: { send_emails: true },
      });
      const incoming = r.data?.items || [];
      // Bepaal nieuw vs reeds gezien
      const newOnes = [];
      const newIds = new Set();
      incoming.forEach((it) => {
        newIds.add(it.id);
        if (!seenIdsRef.current.has(it.id)) {
          newOnes.push(it);
        }
      });
      seenIdsRef.current = newIds;

      setItems(incoming);
      setOpenCount(incoming.length);
      setOverdueCount(incoming.filter((it) => it.is_overdue).length);

      // Bij eerste poll: niet auto-popen; gebruiker kan via badge openen.
      // Bij vervolg-polls: open de modal wanneer er ECHT nieuwe due is.
      if (!initialPollDoneRef.current) {
        initialPollDoneRef.current = true;
        // Als er al wat is bij login → toon discrete toast
        if (incoming.length > 0) {
          toast.info(
            `${incoming.length} reminder${incoming.length === 1 ? "" : "s"} staan open`,
            { description: "Klik op de Bell-badge in de sidebar." },
          );
        }
        return;
      }
      if (newOnes.length > 0) {
        setModalOpen(true);
        playBeep(audioCtxRef);
        // Markeer aan backend dat de pop-up is getoond (idempotent)
        newOnes.forEach((it) => {
          if (!lastShownAtServerRef.current.has(it.id)) {
            lastShownAtServerRef.current.add(it.id);
            adminApi.post(`/admin/reminders/${it.id}/mark-shown`).catch(() => {});
          }
        });
      }
    } catch (err) {
      // Stil falen — geen toast spam
      // eslint-disable-next-line no-console
      console.warn("reminder due poll failed", formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (!canView) return undefined;
    fetchDue();
    const id = setInterval(fetchDue, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [canView, fetchDue]);

  // ---- Actions ----
  const refreshAndMaybeClose = useCallback((removedId) => {
    setItems((prev) => prev.filter((it) => it.id !== removedId));
    seenIdsRef.current.delete(removedId);
    // Volgende poll bevestigt;  als list leeg → sluit modal
    setTimeout(() => {
      setItems((cur) => {
        if (cur.length === 0) {
          setModalOpen(false);
        }
        return cur;
      });
    }, 50);
  }, []);

  const handleSnooze = useCallback(async (item, minutes) => {
    setBusyItem(item.id);
    try {
      await adminApi.post(`/admin/reminders/${item.id}/snooze`, { minutes });
      toast.success(`Gesnoozed (${minutes}m)`);
      refreshAndMaybeClose(item.id);
    } catch (err) {
      toast.error(formatApiError(err) || "Snooze mislukt");
    } finally {
      setBusyItem(null);
    }
  }, [refreshAndMaybeClose]);

  const handleDismiss = useCallback(async (item) => {
    setBusyItem(item.id);
    try {
      await adminApi.post(`/admin/reminders/${item.id}/dismiss`, { minutes: 8 * 60 });
      toast.success("Reminder weggeklikt — komt later terug");
      refreshAndMaybeClose(item.id);
    } catch (err) {
      toast.error(formatApiError(err) || "Wegklikken mislukt");
    } finally {
      setBusyItem(null);
    }
  }, [refreshAndMaybeClose]);

  const handleComplete = useCallback(async (item) => {
    setBusyItem(item.id);
    try {
      await adminApi.post(`/admin/reminders/${item.id}/complete`);
      toast.success("Reminder afgerond");
      refreshAndMaybeClose(item.id);
    } catch (err) {
      toast.error(formatApiError(err) || "Afronden mislukt");
    } finally {
      setBusyItem(null);
    }
  }, [refreshAndMaybeClose]);

  const handleOpen = useCallback((item) => {
    // P1 Bugfix 2 Mrt 2026 — eerst Dialog cleanen, dan pas navigeren.
    // Anders unmount de Dialog mid-Radix-cleanup en blijft
    // body[pointer-events:none] hangen waardoor sidebar bevriest.
    setModalOpen(false);
    setTimeout(() => {
      if (onNavigateToReminder) {
        onNavigateToReminder(item);
      } else {
        // fallback — toon detail via /admin?section=reminders
        window.location.hash = "#reminders";
      }
    }, 50);
  }, [onNavigateToReminder]);

  const openModal = useCallback(() => {
    setModalOpen(true);
    // Geen mark-shown bij handmatig openen — alleen bij echt nieuwe due
  }, []);

  // P1 Bugfix 2 Mrt 2026 — Safety-net voor Radix Dialog cleanup race.
  // Wanneer de modal sluit kan Radix' eigen pointer-events restoration
  // soms blijven hangen (bekende race conditie bij snel achterelkaar
  // mount/unmount of bij navigatie tijdens close). Wij herstellen
  // defensief na 250ms wanneer er geen enkele open Dialog meer is.
  useEffect(() => {
    if (modalOpen) return undefined;
    const t = setTimeout(() => {
      try {
        const stillOpen = document.querySelectorAll(
          '[role="dialog"][data-state="open"], [data-radix-dialog-overlay][data-state="open"]',
        ).length;
        if (stillOpen === 0) {
          const body = document.body;
          // Inline style?  Wis hem.
          if (body.style.pointerEvents === "none") {
            body.style.pointerEvents = "";
          }
          // data-scroll-locked attribute? Wis hem.
          if (body.hasAttribute("data-scroll-locked")) {
            body.removeAttribute("data-scroll-locked");
          }
        }
      } catch { /* DOM weg of niet beschikbaar */ }
    }, 250);
    return () => clearTimeout(t);
  }, [modalOpen]);

  const ctxValue = useMemo(() => ({
    openCount, overdueCount, openModal,
  }), [openCount, overdueCount, openModal]);

  if (!canView) {
    return (
      <ReminderAlertCtx.Provider value={ctxValue}>
        {children}
      </ReminderAlertCtx.Provider>
    );
  }

  return (
    <ReminderAlertCtx.Provider value={ctxValue}>
      {children}

      {/* Floating badge wanneer modal dicht is — handmatig openen */}
      {openCount > 0 && !modalOpen && (
        <button
          type="button"
          onClick={openModal}
          className="hidden sm:flex fixed bottom-6 right-6 z-40 bg-red-600 hover:bg-red-700 text-white shadow-2xl rounded-full px-4 py-3 items-center gap-2 animate-pulse"
          data-testid="reminder-alerts-floating-badge"
          title="Open reminders pop-up"
        >
          <Bell className="w-5 h-5" />
          <span className="font-bold text-sm">{openCount}</span>
          {overdueCount > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] uppercase font-bold">
              {overdueCount} te laat
            </span>
          )}
        </button>
      )}

      <Dialog open={modalOpen} onOpenChange={(o) => setModalOpen(!!o)}>
        <DialogContent
          className="max-w-2xl max-h-[88vh] overflow-y-auto"
          data-testid="reminder-alerts-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-chivo font-black text-lg text-[#082c50] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              {items.length === 0 ? "Geen openstaande reminders" : `${items.length} reminder${items.length === 1 ? "" : "s"} staan open`}
            </DialogTitle>
            <DialogDescription>
              Onderstaande reminders zijn nu actief. Snooze, klik weg, rond af of open ze.
            </DialogDescription>
          </DialogHeader>

          {audioState === "suspended" && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 my-2 flex items-center gap-2 flex-wrap">
              <VolumeX className="w-4 h-4 text-amber-700 flex-shrink-0" />
              <div className="flex-1 text-xs text-amber-900 min-w-[180px]">
                Geluidssignaal is geblokkeerd door uw browser. Klik om te activeren — zo hoort u nieuwe reminders direct.
              </div>
              <Button
                size="sm"
                onClick={enableAudio}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs"
                data-testid="reminder-alerts-enable-audio"
              >
                <Volume2 className="w-3 h-3 mr-1" /> Geluid activeren
              </Button>
            </div>
          )}
          {audioState === "running" && (
            <div className="text-[10px] text-emerald-700 flex items-center gap-1 -mt-1">
              <Volume2 className="w-3 h-3" /> Geluid is actief
            </div>
          )}

          <div className="space-y-2 mt-2" data-testid="reminder-alerts-list">
            {loading && items.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Reminders laden…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-500" data-testid="reminder-alerts-empty">
                Geen openstaande reminders op dit moment.
              </div>
            )}
            {items.map((it) => (
              <ReminderRow
                key={it.id}
                item={it}
                busy={busyItem === it.id}
                onSnooze={handleSnooze}
                onDismiss={handleDismiss}
                onComplete={handleComplete}
                onOpen={handleOpen}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </ReminderAlertCtx.Provider>
  );
}

function ReminderRow({ item, busy, onSnooze, onDismiss, onComplete, onOpen }) {
  const priority = (item.priority || "normaal").toLowerCase();
  const prCls = {
    urgent: "bg-red-100 text-red-800 border-red-300",
    hoog: "bg-orange-100 text-orange-800 border-orange-300",
    normaal: "bg-blue-100 text-blue-800 border-blue-300",
    laag: "bg-gray-100 text-gray-700 border-gray-300",
  }[priority] || "bg-blue-100 text-blue-800 border-blue-300";

  const linkLabel = item.link_type
    ? `${item.link_type}${item.link_id ? ` · ${item.link_id.slice(0, 8)}…` : ""}`
    : null;

  return (
    <div
      className="border border-gray-200 rounded-md p-3 bg-white"
      data-testid={`reminder-alert-row-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 border rounded ${prCls}`}>
              {priority}
            </span>
            {item.is_overdue && (
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded">
                Te laat
              </span>
            )}
            <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" />
              {item.date}
              {item.time_of_day ? ` · ${item.time_of_day}` : ""}
            </span>
          </div>
          <div className="font-bold text-sm text-[#082c50] mt-1">{item.title}</div>
          {item.text && (
            <p className="text-xs text-gray-700 mt-1 leading-snug">{item.text}</p>
          )}
          {linkLabel && (
            <div className="text-[10px] text-gray-500 mt-1 font-mono">{linkLabel}</div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-gray-100">
        <Button
          size="sm" variant="outline"
          disabled={busy}
          onClick={() => onSnooze(item, 15)}
          className="rounded-md text-[10px] h-7 px-2"
          data-testid={`reminder-alert-snooze-15-${item.id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "15m"}
        </Button>
        <Button
          size="sm" variant="outline"
          disabled={busy}
          onClick={() => onSnooze(item, 60)}
          className="rounded-md text-[10px] h-7 px-2"
          data-testid={`reminder-alert-snooze-60-${item.id}`}
        >
          1u
        </Button>
        <Button
          size="sm" variant="outline"
          disabled={busy}
          onClick={() => onSnooze(item, 24 * 60)}
          className="rounded-md text-[10px] h-7 px-2"
          data-testid={`reminder-alert-snooze-day-${item.id}`}
        >
          Morgen
        </Button>
        <Button
          size="sm" variant="outline"
          disabled={busy}
          onClick={() => onDismiss(item)}
          className="rounded-md text-[10px] h-7 px-2"
          data-testid={`reminder-alert-dismiss-${item.id}`}
        >
          <X className="w-3 h-3 mr-0.5" /> Niet nu
        </Button>
        <Button
          size="sm" variant="outline"
          disabled={busy}
          onClick={() => onComplete(item)}
          className="rounded-md text-[10px] h-7 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid={`reminder-alert-complete-${item.id}`}
        >
          <Check className="w-3 h-3 mr-0.5" /> Afronden
        </Button>
        <Button
          size="sm"
          disabled={busy}
          onClick={() => onOpen(item)}
          className="rounded-md text-[10px] h-7 px-2 bg-[#082c50] text-white"
          data-testid={`reminder-alert-open-${item.id}`}
        >
          <ExternalLink className="w-3 h-3 mr-0.5" /> Openen
        </Button>
      </div>
    </div>
  );
}
